// Role of this file:
// Listen for an action
// produce a side effect which is communicate with the server via http,
// bring data back and dispatch a new action (success or fail)

import { Injectable } from "@angular/core";

// Actions is an observable that listens to the types of actions
// being dispatched and we can respond accordingly
import * as fromRoot from "#core/router-store";
import { LocalStorage } from "#core/services/local-storage.service";
import { Asset } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-angular-cure";
import { AssignmentsService, ClService } from "@carnegie-learning/data-access-angular-passport";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { Student } from "cli-types";
import { of } from "rxjs";
import {
    catchError,
    delay,
    map,
    switchMap,
    withLatestFrom,
} from "rxjs/operators";
import * as assignmentsActions from "../actions/assignments.action";
import * as studentAssetsActions from "../actions/student-assets.action";
import { getStudentAssets } from "../selectors/student-assets.selectors";

@Injectable()
export class AssignmentsEffects {
    constructor(
        private actions$: Actions,
        private store: Store<fromRoot.CustomSerializer>,
        private assignmentsService: AssignmentsService,
        private clService: ClService,
        private assetsService: AssetsService,
        private localStorage: LocalStorage
    ) {}

    loadAssignments$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.LoadStudentAssignments))
            .pipe(
                withLatestFrom(
                    this.store.pipe(select(fromRoot.getRouterState)),
                    (action, router) => {
                        const userData: Student = this.localStorage.get(
                            "userProfile"
                        );
                        if (
                            (<any>window).isDemo &&
                            userData &&
                            userData.lastCourse
                        ) {
                            return {
                                ...action.payload,
                                courseID: userData.lastCourse,
                            };
                        } else {
                            return {
                                ...action.payload,
                                courseID: router.state.params.courseID,
                            };
                        }
                    }
                ),
                switchMap((data) => {
                    return this.assignmentsService
                        .getStudentAssignments(data)
                        .pipe(
                            // mapping over all the To Do assignments and returning an action
                            map((assignmentPagination) =>
                                assignmentsActions.LoadStudentAssignmentsSuccess(
                                    {
                                        AssignmentPaginationData: assignmentPagination,
                                    }
                                )
                            ),
                            // need to wrap the error in 'of' so it becomes an observable action
                            catchError((error) =>
                                of(
                                    assignmentsActions.LoadStudentAssignmentsFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );

    loadAssignment$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.LoadStudentAssignment))
            .pipe(
                switchMap((action) => {
                    return this.assignmentsService
                        .getFullAssignment(action.assignmentID)
                        .pipe(
                            // mapping over all the assignments and returning an action
                            switchMap((assignment) => {
                                if (
                                    assignment &&
                                    !assignment.asset &&
                                    assignment.compositeTaskAssignments
                                ) {
                                    // get asset for compositeTaskAssignments
                                    const assetIds = assignment.compositeTaskAssignments.map(
                                        (a) => a.assetId
                                    );
                                    assetIds.push(assignment.assetId);
                                    const params: FeathersServicePaginationParameters<Asset> = {
                                        limit: 100,
                                        skip: 0,
                                        filter: { "assetId[$in][]": assetIds },
                                        sorting: {},
                                    };
                                    return this.assetsService
                                        .getAssets(params)
                                        .pipe(
                                            map((assets) => {
                                                const foundAssignmentAsset = assets.data.find(
                                                    (a) =>
                                                        a.assetId ===
                                                        assignment.assetId
                                                );
                                                if (foundAssignmentAsset) {
                                                    assignment = {
                                                        ...assignment,
                                                        asset: foundAssignmentAsset,
                                                    };
                                                }
                                                assignment.compositeTaskAssignments = assignment.compositeTaskAssignments.map(
                                                    (task) => {
                                                        task.asset = assets.data.find(
                                                            (asset) =>
                                                                asset.assetId ===
                                                                task.assetId
                                                        );
                                                        return task;
                                                    }
                                                );
                                                return assignmentsActions.updateStudentAssignmentSuccess(
                                                    {
                                                        assignment: assignment,
                                                    }
                                                );
                                            }),
                                            catchError((error) =>
                                                of(
                                                    assignmentsActions.LoadAssignmentAssetFail(
                                                        error
                                                    )
                                                )
                                            )
                                        );
                                } else {
                                    return of(
                                        assignmentsActions.LoadStudentAssignmentSuccess(
                                            {
                                                assignment: assignment,
                                            }
                                        )
                                    );
                                }
                            }),
                            catchError((error) =>
                                of(
                                    assignmentsActions.LoadStudentAssignmentFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );

    updateAssignment$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.updateStudentAssignment))
            .pipe(
                switchMap((action) => {
                    return this.assignmentsService
                        .getFullAssignment(action.assignmentID)
                        .pipe(
                            // mapping over all the assignments and returning an action
                            switchMap((assignment) => {
                                if (assignment.compositeTaskAssignments) {
                                    // get asset for compositeTaskAssignments
                                    const assetIds = assignment.compositeTaskAssignments.map(
                                        (a) => a.assetId
                                    );
                                    const params: FeathersServicePaginationParameters<Asset> = {
                                        limit: 100,
                                        skip: 0,
                                        filter: { "assetId[$in][]": assetIds },
                                        sorting: {},
                                    };
                                    return this.assetsService
                                        .getAssets(params)
                                        .pipe(
                                            map((assets) => {
                                                assignment.compositeTaskAssignments = assignment.compositeTaskAssignments.map(
                                                    (task) => {
                                                        task.asset = assets.data.find(
                                                            (asset) =>
                                                                asset.assetId ===
                                                                task.assetId
                                                        );
                                                        return task;
                                                    }
                                                );
                                                return assignmentsActions.updateStudentAssignmentSuccess(
                                                    {
                                                        assignment: assignment,
                                                    }
                                                );
                                            }),
                                            catchError((error) =>
                                                of(
                                                    assignmentsActions.LoadAssignmentAssetFail(
                                                        error
                                                    )
                                                )
                                            )
                                        );
                                } else {
                                    return of(
                                        assignmentsActions.LoadStudentAssignmentSuccess(
                                            {
                                                assignment: assignment,
                                            }
                                        )
                                    );
                                }
                            }),
                            catchError((error) =>
                                of(
                                    assignmentsActions.updateStudentAssignmentFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );

    loadAssignmentAsset$ = createEffect(() =>
        this.actions$.pipe(ofType(assignmentsActions.LoadAssignmentAsset)).pipe(
            switchMap((action) => {
                return this.assetsService.getSelectedAsset(action.assetId).pipe(
                    map((asset) =>
                        assignmentsActions.LoadAssignmentAssetSuccess({
                            assignmentId: action.assignmentId,
                            asset: asset,
                        })
                    ),
                    catchError((error) =>
                        of(assignmentsActions.LoadAssignmentAssetFail(error))
                    )
                );
            })
        )
    );

    loadAllStudentAssignmentAssets$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.LoadAllStudentAssignmentAssets))
            .pipe(
                delay(100),
                withLatestFrom(this.store.pipe(select(getStudentAssets))),
                switchMap(([{ assignments }, storedAssets]) => {
                    const storedAssetIds = storedAssets.map((a) => a.assetId);
                    const assetIds = assignments
                        .map((a) => a.assetId)
                        .filter((id) => !!id && !storedAssetIds.includes(id));
                    const params: FeathersServicePaginationParameters<Asset> = {
                        limit: 100,
                        skip: 0,
                        filter: { "assetId[$in][]": assetIds },
                        sorting: {},
                    };
                    if (!assetIds.length) {
                        return of(
                            assignmentsActions.LoadAllStudentAssignmentAssetsSuccess(
                                {
                                    assignments,
                                    assets: storedAssets,
                                }
                            )
                        );
                    }
                    return this.assetsService.getAssets(params).pipe(
                        switchMap((assets) => [
                            assignmentsActions.LoadAllStudentAssignmentAssetsSuccess(
                                {
                                    assignments,
                                    assets: storedAssets.concat(assets.data),
                                }
                            ),
                            studentAssetsActions.LoadAssetsSuccess({
                                payload: assets,
                            }),
                        ]),
                        catchError((error) =>
                            of(
                                assignmentsActions.LoadAllStudentAssignmentAssetsFail(
                                    error
                                )
                            )
                        )
                    );
                })
            )
    );

    loadBundleAssignmentsAssets$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.loadBundleAssignmentsAssets))
            .pipe(
                switchMap((action) => {
                    const assetIds = action.compositeTaskAssignments.map(
                        (a) => a.assetId
                    );
                    const params: FeathersServicePaginationParameters<Asset> = {
                        limit: 100,
                        skip: 0,
                        filter: { "assetId[$in][]": assetIds },
                        sorting: {},
                    };
                    return this.assetsService.getAssets(params).pipe(
                        map((assets) =>
                            assignmentsActions.loadBundleAssignmentsAssetsSuccess(
                                {
                                    bundleAssetId: action.bundleAssetId,
                                    compositeTaskAssignments:
                                        action.compositeTaskAssignments,
                                    assets: assets.data,
                                }
                            )
                        ),
                        catchError((error) =>
                            of(
                                assignmentsActions.LoadAssignmentAssetFail(
                                    error
                                )
                            )
                        )
                    );
                })
            )
    );
    getAssignmentFromTransactionId$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.SetStudentTransIdForAssignment))
            .pipe(
                switchMap((action) => {
                    return this.clService
                        .getStudentAssignmentByTransaction(action.transactionID)
                        .pipe(
                            // mapping over the assignment and returning an action
                            map((response) =>
                                assignmentsActions.GetStudentAssignmentFromTransIdSuccess(
                                    {
                                        assignment: response,
                                    }
                                )
                            ),
                            // need to wrap the error in 'of' so it becomes an observable action
                            catchError((error) =>
                                of(
                                    assignmentsActions.GetStudentAssignmentFromTransIdFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );
    setAssignmentMarkedAsRead$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assignmentsActions.setAssignmentMarkedAsRead))
            .pipe(
                switchMap(({ assignment }) => {
                    return this.assignmentsService
                        .setMarkedAsRead(assignment.assignmentID)
                        .pipe(
                            map(() =>
                                assignmentsActions.setAssignmentMarkedAsReadSuccess()
                            ),
                            catchError((error: Error) =>
                                of(
                                    assignmentsActions.setAssignmentMarkedAsReadFail(
                                        { payload: error }
                                    )
                                )
                            )
                        );
                })
            )
    );
}
